﻿import { LoadingButton } from "@mui/lab";
import { Grid, IconButton, Modal, Slider, Stack, Typography, Alert, Snackbar } from "@mui/material";
import { Mp3Encoder } from "lamejs";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RecordIcon from "@mui/icons-material/FiberManualRecord";
import PauseIcon from "@mui/icons-material/PauseRounded";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import { default as StopIcon, default as StopRoundedIcon } from "@mui/icons-material/StopRounded";
import MicIcon from "@mui/icons-material/Mic";
import VolumeReactiveVisualizer from "../../common/VolumeReactiveVisualizer";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import { SAVE_CHAT_MEDIA_MASSAGES } from "../../constant/Apipath";
import SendIcon from "@mui/icons-material/Send";
import ReplayIcon from "@mui/icons-material/Replay";

export default function ChatAudioRecorder({
  isOpen,
  onClose,
  sendAudio,
  TEXT_RECORD_A_MASSAGE,
  TEXT_SAVE,
  TEXT_SEND,
  uploadUrl = SAVE_CHAT_MEDIA_MASSAGES,
  clientId,
  accentColor = "#083db8"
}) {
  const { loginData } = useSelector((state) => state?.main);

  const audioRef = useRef(null);
  const recordingInterval = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [blobMp3, setBlobMp3] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [currentState, setCurrentState] = useState("Home");
  const [recordingTime, setRecordingTime] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [micPermission, setMicPermission] = useState(null);
  const [isRecording, setIsRecording] = useState(false);

  // Check for microphone permission when component mounts
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        const result = await navigator.mediaDevices.getUserMedia({ audio: true });
        // If successful, clean up the stream immediately
        if (result) {
          const tracks = result.getTracks();
          tracks.forEach((track) => track.stop());
          setMicPermission(true);
        }
      } catch (err) {
        console.error("checkMicPermission-> Microphone permission denied: ", err);
        setMicPermission(false);
        setErrorMessage("Microphone access is required to record.");
        setShowError(true);
      }
    };

    if (isOpen) {
      checkMicPermission();
    }

    return () => {
      // Clean up resources when component unmounts
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      clearInterval(recordingInterval.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isOpen]);

  // Request microphone permission
  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Clean up immediately
      setMicPermission(true);
      setShowError(false);
    } catch (err) {
      console.error("requestMicPermission-> Couldn't get microphone permission: ", err);
      setErrorMessage("Please allow microphone access to record audio.");
      setShowError(true);
      setMicPermission(false);
    }
  };

  const convertToMp3 = async (audioBlob) => {
    if (!audioBlob || !(audioBlob instanceof Blob)) {
      setErrorMessage("Invalid audio data received.");
      setShowError(true);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const arrayBuffer = reader.result;
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          // audioBuffer.duration is the exact, reliable length in seconds.
          // lamejs MP3s report Infinity via the <audio> element, so capture it here.
          if (Number.isFinite(audioBuffer.duration) && audioBuffer.duration > 0) {
            setDuration(audioBuffer.duration);
          }
          const samples = audioBuffer.getChannelData(0);
          const samplesInt16 = new Int16Array(samples?.length);

          for (let i = 0; i < samples?.length; i++) {
            samplesInt16[i] = samples[i] < 0 ? samples[i] * 0x8000 : samples[i] * 0x7fff;
          }

          const mp3Encoder = new Mp3Encoder(1, audioBuffer.sampleRate, 128);
          const mp3Data = [];

          const mp3Buf = mp3Encoder.encodeBuffer(samplesInt16);
          if (mp3Buf?.length > 0) {
            mp3Data.push(mp3Buf);
          }

          const flushBuf = mp3Encoder.flush();
          if (flushBuf?.length > 0) {
            mp3Data.push(flushBuf);
          }

          const mp3Blob = new Blob(mp3Data, { type: "audio/mp3" });
          setBlobMp3(mp3Blob);

          // Clean up previous audio URL if it exists
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
          }

          const mp3Url = URL.createObjectURL(mp3Blob);
          setAudioUrl(mp3Url);

          // **IMPORTANT**: Close audio context to prevent memory leaks
          if (audioContext.state !== "closed") {
            audioContext.close();
          }
        } catch (err) {
          console.error("convertToMp3-> Error converting audio: ", err);
          setErrorMessage("Error processing audio recording.");
          setShowError(true);
        }
      };

      reader.onerror = () => {
        setErrorMessage("Error reading audio file.");
        setShowError(true);
      };

      reader.readAsArrayBuffer(audioBlob);
    } catch (err) {
      console.error("convertToMp3-> Error in MP3 conversion: ", err);
      setErrorMessage("Error converting audio to MP3.");
      setShowError(true);
    }
  };

  const onSavePressed = async () => {
    if (!blobMp3 || !(blobMp3 instanceof Blob)) {
      setErrorMessage("No valid recording found to save.");
      setShowError(true);
      return;
    }
    setSaveLoading(true);
    try {
      const exactDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
      const recordedDuration = Math.max(1, Math.round(exactDuration || recordingTime || 0));
      console.log("ChatAudioRecorder-> sending duration (seconds):", recordedDuration);
      const formData = new FormData();
      if (clientId) {
        formData.append("ClientId", clientId);
        formData.append("Files", blobMp3, "recording.mp3");
      } else {
        formData.append("file", blobMp3, "recording.mp3");
      }
      const url = await Fileuploadrequestcall(uploadUrl, formData, loginData?.accessToken);
      if (url) {
        const media = url?.data?.data?.[0];
        // For the user chat the backend prepends the storage container URL, so
        // send only the filename (full URL would be double-prefixed). The client
        // chat sends the full URL wrapped in JSON instead.
        const audioValue = clientId ? media?.fileUrl?.[0] || media?.fileName : media?.fileName;
        await sendAudio(audioValue, recordedDuration);
        resetData();
      }
    } catch (error) {
      console.error("onSavePressed-> Error While Saving Audio File: ", error);
      setErrorMessage("Failed to save recording.");
      setShowError(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;

    const time = e.target.value;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;

    setCurrentTime(audioRef.current.currentTime);

    if (audioRef.current.currentTime >= audioRef.current.duration) {
      console.log("Audio playback finished");
      setIsPlaying(false);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
    }
  };

  const startRecordingTimer = () => {
    clearInterval(recordingInterval.current);
    recordingInterval.current = setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);
  };

  const resetRecording = () => {
    clearInterval(recordingInterval.current);
    setCurrentState("Home");
    setIsPaused(false);
    setRecordingTime(0);
    setIsRecording(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const resetData = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      clearInterval(recordingInterval.current);
      setCurrentState("Home");
      setIsPlaying(false);
      setRecordingTime(0);
      setIsPaused(false);
      setAudioUrl(null);
      setBlobMp3(null);
      setAudioBlob(null);
      setDuration(0);
      setCurrentTime(0);
      setIsRecording(false);
      setSaveLoading(false);
      setShowError(false);
      setErrorMessage("");
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      onClose();
    } catch (e) {
      console.error("Error in resetData:", e);
    }
  };

  const handleStartRecording = async () => {
    if (micPermission === false) {
      await requestMicPermission();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        setAudioBlob(audioBlob);
        convertToMp3(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      startRecordingTimer();
      setCurrentState("Recording");
    } catch (err) {
      console.error("handleStartRecording-> Failed to start recording: ", err);
      setErrorMessage("Failed to start recording. Please check microphone permissions.");
      setShowError(true);
    }
  };

  const handlePauseRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        clearInterval(recordingInterval.current);
      }
    } catch (err) {
      console.error("Error pausing:", err);
      setErrorMessage("Error pausing recording.");
      setShowError(true);
    }
  };

  const handleResumeRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        startRecordingTimer();
      }
    } catch (err) {
      console.error("Error resuming:", err);
      setErrorMessage("Error resuming recording.");
      setShowError(true);
    }
  };

  const handleStopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        setCurrentState("Playback");
        setIsRecording(false);
        clearInterval(recordingInterval.current);
      }
    } catch (err) {
      console.error("Error stopping:", err);
      setErrorMessage("Error stopping recording.");
      setShowError(true);
    }
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <>
      <Modal open={isOpen} onClose={resetData}>
        <div className="client-modal-responsive">
          <div className="action-button-container">
            <IconButton onClick={resetData}>
              <CloseIcon />
            </IconButton>
          </div>

          <Snackbar open={showError} autoHideDuration={6000} onClose={handleCloseError}>
            <Alert onClose={handleCloseError} severity="error" sx={{ width: "100%" }}>
              {errorMessage}
            </Alert>
          </Snackbar>

          <Grid container sx={{ display: "flex", flexDirection: "column" }}>
            {currentState === "Home" && (
              <Stack sx={{ display: "flex",  justifyContent: "center", alignItems: "center" }}  height={"200px"}>
                <Typography style={{ color: "lightgrey" }}>
                  {micPermission === false
                    ? "Microphone permission required"
                    : TEXT_RECORD_A_MASSAGE}
                </Typography>
              </Stack>
            )}

            {currentState === "Recording" && (
              <>
                <Stack sx={{ display: "flex",  alignItems: "center", justifyContent: "center", border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    margin: "10px",
                    backgroundColor: "#fafafa" }} position={"relative"} height={"200px"} >
                  {/* **UPDATED**: Using your custom VolumeReactiveVisualizer */}
                  {mediaRecorderRef.current && (
                    <VolumeReactiveVisualizer
                      mediaRecorder={mediaRecorderRef.current}
                      width="100%"
                      height={180}
                      barColor="grey"
                    />
                  )}
                </Stack>

                <Stack sx={{ gap: 1,  display: "flex",  margin: "10px 0",  justifyContent: "center", alignItems: "center"  }}  direction={"row"}  >
                  <span
                    className={!isPaused ? "pulse" : ""}
                    style={{
                      backgroundColor: "red",
                      height: "12px",
                      width: "12px",
                      borderRadius: "50%",
                      border: "2px solid white",
                      boxShadow: "0 0 5px rgba(255, 0, 0, 0.5)"
                    }}
                  />
                  <Typography style={{ fontWeight: "bold", fontSize: "18px" }}>
                    {moment.utc(recordingTime * 1000).format("m:ss")}
                  </Typography>
                </Stack>
              </>
            )}

            {currentState === "Playback" && audioUrl && (
              <Grid container sx={{ padding: 1 }} size={12}>
                <Grid size={12}>
                  <Slider
                    fullWidth
                    value={currentTime || 0}
                    min={0}
                    step={0.01}
                    max={duration || 0}
                    onChange={handleSeek}
                    aria-labelledby="audio-slider"
                  />
                  <Stack sx={{ display: "flex", justifyContent: "space-between" }} direction={"row"}>
                    <Typography>{moment.utc(currentTime * 1000).format("m:ss")}</Typography>
                    <Typography>{moment.utc(duration * 1000).format("m:ss")}</Typography>
                  </Stack>
                </Grid>
                <audio
                  type="audio/mp3"
                  hidden
                  controls
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  name="media"
                  ref={audioRef}
                  src={audioUrl}
                />
              </Grid>
            )}
          </Grid>

          <Grid container sx={{ display: "flex", flexDirection: "column" }}>
            {currentState === "Home" && (
              <Stack sx={{ gap: 2,  display: "flex",  marginTop: "20px",  justifyContent: "center", alignItems: "center"  }}   >
                {micPermission === false ? (
                  <IconButton
                    onClick={requestMicPermission}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      padding: "0px",
                      height: "45px",
                      width: "45px"
                    }}>
                    <MicIcon
                      style={{
                        fontSize: "25px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleStartRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      padding: "0px",
                      height: "45px",
                      width: "45px"
                    }}>
                    <RecordIcon
                      style={{
                        fontSize: "25px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                )}
              </Stack>
            )}
            {currentState === "Recording" && (
              <Stack sx={{ gap: 2,  display: "flex",  marginTop: "20px",  alignItems: "center", justifyContent: "center"  }}   direction={"row"} >
                {!isPaused ? (
                  <IconButton
                    onClick={handlePauseRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "45px",
                      height: "45px"
                    }}>
                    <PauseIcon
                      style={{
                        fontSize: "25px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleResumeRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "45px",
                      height: "45px"
                    }}>
                    <RecordIcon
                      style={{
                        fontSize: "25px",
                        color: "red"
                      }}
                    />
                  </IconButton>
                )}
                <IconButton
                  onClick={handleStopRecording}
                  style={{
                    backgroundColor: "#ebebeb",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "50%",
                    width: "45px",
                    height: "45px"
                  }}>
                  <StopIcon
                    style={{
                      fontSize: "25px",
                      color: "red"
                    }}
                  />
                </IconButton>
              </Stack>
            )}
            {currentState === "Playback" && (
              <>
                <Stack sx={{ gap: 2,  display: "flex",  marginTop: "20px",  alignItems: "center", justifyContent: "center"  }}   direction={"row"} >
                  {isPlaying ? (
                    <IconButton
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.pause();
                          setIsPlaying(false);
                        }
                      }}
                      style={{
                        backgroundColor: "#ebebeb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        width: "45px",
                        height: "45px"
                      }}>
                      <StopRoundedIcon
                        style={{
                          fontSize: "25px",
                          color: "red"
                        }}
                      />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.currentTime = 0;
                          audioRef.current.play().catch((err) => {
                            console.error("Error playing audio:", err);
                            setErrorMessage("Error playing audio file.");
                            setShowError(true);
                          });
                          setIsPlaying(true);
                        }
                      }}
                      style={{
                        backgroundColor: "#ebebeb",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        width: "45px",
                        height: "45px"
                      }}>
                      <PlayIcon
                        style={{
                          fontSize: "25px"
                        }}
                      />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={resetRecording}
                    style={{
                      backgroundColor: "#ebebeb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "45px",
                      height: "45px"
                    }}>
                    <DeleteIcon
                      style={{
                        fontSize: "25px"
                      }}
                    />
                  </IconButton>
                </Stack>
                <Grid container size={{ xs: 12 }} sx={{ gap: '15px',  justifyContent: "center"  }} >
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <LoadingButton
                      onClick={resetRecording}
                      disabled={saveLoading}
                      loadingPosition="start"
                      startIcon={<ReplayIcon />}
                      fullWidth
                      style={{
                        textTransform: "none",
                        // backgroundColor: "#524e4e",
                        backgroundColor: saveLoading ? "#ebebeb" : accentColor,
                        color: saveLoading?"#524e4e": "white",
                        marginTop: "20px",
                        borderRadius: "10px",
                        padding: "8px"
                      }}>
                      {"Record Again"}
                    </LoadingButton>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <LoadingButton
                      onClick={onSavePressed}
                      loading={saveLoading}
                      loadingPosition="start"
                      startIcon={<SendIcon />}
                      fullWidth
                      style={{
                        textTransform: "none",
                        backgroundColor: accentColor,
                        color: "white",
                        marginTop: "20px",
                        borderRadius: "10px",
                        padding: "8px"
                      }}>
                      {TEXT_SEND}
                    </LoadingButton>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </div>
      </Modal>
    </>
  );
}

